import { useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import '../../styles/DataTable.css';

const OPCIONES_PAGE_SIZE = [10, 20, 50, 'todos'];

/**
 * Tabla de datos genérica del panel (HU-004 Fase 7) — envuelve `react-table`
 * (ya instalado, sin agregar dependencia) para dar orden por columna y una
 * apariencia consistente en toda la consola, en vez de que cada pantalla
 * arme su propia tabla `<table>` a mano. `columns`/`data` siguen el formato
 * estándar de react-table (`accessor`, `Header`, `Cell` opcional).
 * `onRowClick(row.original)` es opcional — si se pasa, la fila entera queda
 * clickeable (usado por Tickets para ir al detalle del ticket en su propia
 * página).
 *
 * `paginacion` (opcional) es server-side — la tabla no filtra/pagina nada
 * por su cuenta, solo dibuja los controles: `{ page, pageSize, total,
 * onPageChange, onPageSizeChange }`. Sin esta prop, la tabla se muestra sin
 * pie de paginación (ej. dentro de un modal con pocos resultados).
 *
 * Mobile (HU-011): la misma tabla se convierte en una lista de tarjetas
 * tipo acordeón por CSS (`DataTable.css`) — cada `<td>` lleva `data-label`
 * con el nombre de su columna (para el `::before` que lo muestra), y cada
 * fila arranca colapsada mostrando solo la primera celda + un chevron para
 * expandir el resto. No se duplica el render en dos estructuras distintas,
 * es la misma tabla con otro layout.
 */
const DataTable = ({ columns, data, onRowClick, filaVacia = 'Sin resultados.', paginacion }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy
  );
  const [filasExpandidas, setFilasExpandidas] = useState(() => new Set());

  const toggleExpandida = (rowId, evento) => {
    evento.stopPropagation();
    setFilasExpandidas((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(rowId)) nuevo.delete(rowId); else nuevo.add(rowId);
      return nuevo;
    });
  };

  return (
    <div className="panel-datatable-wrap">
      <table className="panel-datatable" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th key={columnKey} {...columnProps}>
                      {column.render('Header')}
                      {column.isSorted && (
                        <i className={`fas fa-caret-${column.isSortedDesc ? 'down' : 'up'} panel-datatable-orden`}></i>
                      )}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { key: rowKey, ...rowProps } = row.getRowProps();
            const expandida = filasExpandidas.has(row.id);
            return (
              <tr
                key={rowKey}
                {...rowProps}
                className={`${onRowClick ? 'panel-datatable-fila-clickeable' : ''} ${expandida ? 'panel-datatable-fila-expandida' : ''}`}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.cells.map((cell, indice) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps();
                  return (
                    <td key={cellKey} {...cellProps} data-label={String(cell.column.Header || '')}>
                      {indice === 0 && (
                        <button
                          type="button"
                          className="panel-datatable-chevron-movil"
                          onClick={(evento) => toggleExpandida(row.id, evento)}
                          aria-label={expandida ? 'Contraer' : 'Expandir'}
                        >
                          <i className={`fas fa-chevron-${expandida ? 'up' : 'down'}`}></i>
                        </button>
                      )}
                      <span className="panel-datatable-valor">{cell.render('Cell')}</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="panel-datatable-vacio">{filaVacia}</td></tr>
          )}
        </tbody>
      </table>

      {paginacion && (
        <div className="panel-datatable-pie">
          <div className="panel-datatable-pie-tamano">
            <span>Filas por página</span>
            <select
              className="panel-input"
              value={paginacion.pageSize}
              onChange={(e) => paginacion.onPageSizeChange(e.target.value === 'todos' ? 'todos' : Number(e.target.value))}
            >
              {OPCIONES_PAGE_SIZE.map((opcion) => (
                <option key={opcion} value={opcion}>{opcion === 'todos' ? 'Todos' : opcion}</option>
              ))}
            </select>
          </div>

          <span className="panel-texto-ayuda">
            {paginacion.total === 0
              ? 'Sin resultados'
              : `Mostrando ${(paginacion.page - 1) * paginacion.pageSizeEfectivo + 1}–${Math.min(paginacion.page * paginacion.pageSizeEfectivo, paginacion.total)} de ${paginacion.total}`}
          </span>

          <div className="panel-datatable-pie-nav">
            <button
              type="button"
              className="panel-boton-secundario"
              disabled={paginacion.page <= 1}
              onClick={() => paginacion.onPageChange(paginacion.page - 1)}
            >
              Anterior
            </button>
            <button
              type="button"
              className="panel-boton-secundario"
              disabled={paginacion.page * paginacion.pageSizeEfectivo >= paginacion.total}
              onClick={() => paginacion.onPageChange(paginacion.page + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
